import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
// Route wrappers stay eager — needed synchronously to resolve each route.
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { PERMISSIONS } from '@/lib/permissions';

/**
 * Every admin page is code-split via React.lazy so the login/dashboard bundle
 * no longer ships the entire admin SPA. The Suspense fallback (PageSkeleton)
 * lives in App.tsx; pages are grouped per nav area by manualChunks in
 * vite.config.ts.
 */
const CustomerListPage = lazy(() => import('../pages/admin/customers/CustomerListPage'));
const CustomerDetailPage = lazy(() => import('../pages/admin/customers/CustomerDetailPage'));
const CustomerCreatePage = lazy(() => import('../pages/admin/customers/CustomerCreatePage'));
const CustomerEditPage = lazy(() => import('../pages/admin/customers/CustomerEditPage'));
const CustomerNotesPage = lazy(() => import('../pages/admin/customers/CustomerNotesPage'));
const CustomerSystemLogPage = lazy(() => import('../pages/admin/customers/CustomerSystemLogPage'));
const CustomerContactsPage = lazy(() => import('../pages/admin/customers/CustomerContactsPage'));
const CustomerContractsPage = lazy(() => import('../pages/admin/customers/CustomerContractsPage'));
const CustomerCreditPage = lazy(() => import('../pages/admin/customers/CustomerCreditPage'));
const CustomerDepositsPage = lazy(() => import('../pages/admin/customers/CustomerDepositsPage'));
const CustomerInventoryPage = lazy(() => import('../pages/admin/customers/CustomerInventoryPage'));
const OrderListPage = lazy(() => import('../pages/admin/orders/OrderListPage'));
const OrderDetailPage = lazy(() => import('../pages/admin/orders/OrderDetailPage'));
const OrderCreatePage = lazy(() => import('../pages/admin/orders/OrderCreatePage'));
const OrderEditPage = lazy(() => import('../pages/admin/orders/OrderEditPage'));
const OrderApprovalQueuePage = lazy(() => import('../pages/admin/orders/OrderApprovalQueuePage'));
const ContractListPage = lazy(() => import('../pages/admin/contracts/ContractListPage'));
const OfferListPage = lazy(() => import('../pages/admin/offers/OfferListPage'));
const OfferCreatePage = lazy(() => import('../pages/admin/offers/OfferCreatePage'));
const OfferDetailPage = lazy(() => import('../pages/admin/offers/OfferDetailPage'));
const OfferEditPage = lazy(() => import('../pages/admin/offers/OfferEditPage'));
const LeadsListPage = lazy(() => import('../pages/admin/leads/LeadsListPage'));
const LeadsBoardPage = lazy(() => import('../pages/admin/leads/LeadsBoardPage'));
const LeadCreatePage = lazy(() => import('../pages/admin/leads/LeadCreatePage'));
const LeadDetailPage = lazy(() => import('../pages/admin/leads/LeadDetailPage'));
const LeadsReportPage = lazy(() => import('../pages/admin/leads/LeadsReportPage'));
const ProviderListPage = lazy(() => import('../pages/admin/providers/ProviderListPage'));
const ProviderDetailPage = lazy(() => import('../pages/admin/providers/ProviderDetailPage'));
const ProviderCreatePage = lazy(() => import('../pages/admin/providers/ProviderCreatePage'));
const ProviderEditPage = lazy(() => import('../pages/admin/providers/ProviderEditPage'));
const ProviderDepositsListPage = lazy(() => import('../pages/admin/providers/ProviderDepositsListPage'));
const ProductListPage = lazy(() => import('../pages/admin/products/ProductListPage'));
const ProductDetailPage = lazy(() => import('../pages/admin/products/ProductDetailPage'));
const ProductCreatePage = lazy(() => import('../pages/admin/products/ProductCreatePage'));
const ProductEditPage = lazy(() => import('../pages/admin/products/ProductEditPage'));
const ProductInventoryConfigPage = lazy(() => import('../pages/admin/products/ProductInventoryConfigPage'));
const BillingSettingsPage = lazy(() => import('../pages/admin/settings/BillingSettingsPage'));
const LanguagesSettingsPage = lazy(() => import('../pages/admin/settings/LanguagesSettingsPage'));
const PaymentMethodsSettingsPage = lazy(() => import('../pages/admin/settings/PaymentMethodsSettingsPage'));
const PaymentGatewaySettingsPage = lazy(() => import('../pages/admin/settings/PaymentGatewaySettingsPage'));
const InvoiceTemplatesPage = lazy(() => import('../pages/admin/settings/InvoiceTemplatesPage'));
const DocumensoSettingsPage = lazy(() => import('../pages/admin/settings/DocumensoSettingsPage'));
const AnthropicSettingsPage = lazy(() => import('../pages/admin/settings/AnthropicSettingsPage'));
const InventoryStatusesPage = lazy(() => import('../pages/admin/settings/InventoryStatusesPage'));
const FeesPage = lazy(() => import('../pages/admin/settings/FeesPage'));
const CustomFieldsPage = lazy(() => import('../pages/admin/settings/CustomFieldsPage'));
const WebhooksPage = lazy(() => import('../pages/admin/settings/WebhooksPage'));
const PauseTypesPage = lazy(() => import('../pages/admin/settings/PauseTypesPage'));
const ProviderTypesPage = lazy(() => import('../pages/admin/settings/ProviderTypesPage'));
const ProductTypesPage = lazy(() => import('../pages/admin/settings/ProductTypesPage'));
const SlaPoliciesPage = lazy(() => import('../pages/admin/settings/SlaPoliciesPage'));
const CannedResponsesPage = lazy(() => import('../pages/admin/settings/CannedResponsesPage'));
const TicketTagsPage = lazy(() => import('../pages/admin/settings/TicketTagsPage'));
const ChannelsSettingsPage = lazy(() => import('../pages/admin/settings/ChannelsSettingsPage'));
// AuditLogsPage removed — duplicate route cleaned up (R-10, Issue #157 §4)
const SubscriptionListPage = lazy(() => import('../pages/admin/subscriptions/SubscriptionListPage'));
const SubscriptionDetailPage = lazy(() => import('../pages/admin/subscriptions/SubscriptionDetailPage'));
const SubscriptionCreatePage = lazy(() => import('../pages/admin/subscriptions/SubscriptionCreatePage'));
const InvoiceListPage = lazy(() => import('../pages/admin/invoices/InvoiceListPage'));
const InvoiceDetailPage = lazy(() => import('../pages/admin/invoices/InvoiceDetailPage'));
const InvoiceCreatePage = lazy(() => import('../pages/admin/invoices/InvoiceCreatePage'));
const InvoiceEditPage = lazy(() => import('../pages/admin/invoices/InvoiceEditPage'));
const PaymentListPage = lazy(() => import('../pages/admin/payments/PaymentListPage'));
const PaymentDetailPage = lazy(() => import('../pages/admin/payments/PaymentDetailPage'));
const GatewayTransactionLogPage = lazy(() => import('../pages/admin/payments/GatewayTransactionLogPage'));
const DunningQueuePage = lazy(() => import('../pages/admin/billing/DunningQueuePage'));
const EmailReviewQueuePage = lazy(() => import('../pages/admin/operations/EmailReviewQueuePage'));
const LlmUsagePage = lazy(() => import('../pages/admin/operations/LlmUsagePage'));
const GatewayTransactionDetailPage = lazy(() => import('../pages/admin/payments/GatewayTransactionDetailPage'));
const DisputeListPage = lazy(() => import('../pages/admin/payments/DisputeListPage'));
const DisputeDetailPage = lazy(() => import('../pages/admin/payments/DisputeDetailPage'));
const ReconciliationTransactionListPage = lazy(() => import('../pages/admin/reconciliation/ReconciliationTransactionListPage'));
const ReconciliationTransactionDetailPage = lazy(() => import('../pages/admin/reconciliation/ReconciliationTransactionDetailPage'));
const UserListPage = lazy(() => import('../pages/admin/users/UserListPage'));
const UserCreatePage = lazy(() => import('../pages/admin/users/UserCreatePage'));
const UserEditPage = lazy(() => import('../pages/admin/users/UserEditPage'));
const UserDetailPage = lazy(() => import('../pages/admin/users/UserDetailPage'));
// W1.2 — own-account security page (2FA enrol/disable/regenerate).
const ProfileSecurityPage = lazy(() => import('../pages/admin/profile/ProfileSecurityPage'));
const RolesPage = lazy(() => import('../pages/admin/settings/RolesPage'));
const SecuritySettingsPage = lazy(() => import('../pages/admin/settings/SecuritySettingsPage'));
const CompanySettingsPage = lazy(() => import('../pages/admin/settings/CompanySettingsPage'));
const EmailSettingsPage = lazy(() => import('../pages/admin/settings/EmailSettingsPage'));
const TicketSettingsPage = lazy(() => import('../pages/admin/settings/TicketSettingsPage'));
const ContactDefaultsPage = lazy(() => import('../pages/admin/settings/ContactDefaultsPage'));
const ContactRolesPage = lazy(() => import('../pages/admin/settings/ContactRolesPage'));
const WorkflowSettingsPage = lazy(() => import('../pages/admin/settings/WorkflowSettingsPage'));
const WorkflowHealthPage = lazy(() => import('../pages/admin/settings/WorkflowHealthPage'));
const ContractTemplatesPage = lazy(() => import('../pages/admin/settings/ContractTemplatesPage'));
const WorkflowEditorPage = lazy(() => import('../pages/admin/settings/WorkflowEditorPage'));
const WorkflowWizardPage = lazy(() => import('../pages/admin/settings/WorkflowWizardPage'));
const RevenueReportPage = lazy(() => import('../pages/admin/reports/RevenueReportPage'));
const MrrWaterfallReportPage = lazy(() => import('../pages/admin/reports/MrrWaterfallReportPage'));
const SubscriptionReportPage = lazy(() => import('../pages/admin/reports/SubscriptionReportPage'));
const TicketReportPage = lazy(() => import('../pages/admin/reports/TicketReportPage'));
const DepositsReportPage = lazy(() => import('../pages/admin/reports/DepositsReportPage'));
const TicketListPage = lazy(() => import('../pages/admin/tickets').then((m) => ({ default: m.TicketListPage })));
const TicketDetailPage = lazy(() => import('../pages/admin/tickets').then((m) => ({ default: m.TicketDetailPage })));
const TicketCreatePage = lazy(() => import('../pages/admin/tickets').then((m) => ({ default: m.TicketCreatePage })));
const TicketDashboardPage = lazy(() => import('../pages/admin/tickets/TicketDashboardPage'));
const MarketingDashboardPage = lazy(() => import('../pages/admin/marketing/MarketingDashboardPage'));
const CouponsListPage = lazy(() => import('../pages/admin/marketing/CouponsListPage'));
const CouponCreatePage = lazy(() => import('../pages/admin/marketing/CouponCreatePage'));
const CouponEditPage = lazy(() => import('../pages/admin/marketing/CouponEditPage'));
const AffiliatesListPage = lazy(() => import('../pages/admin/marketing/AffiliatesListPage'));
const AffiliateCreatePage = lazy(() => import('../pages/admin/marketing/AffiliateCreatePage'));
const AffiliateEditPage = lazy(() => import('../pages/admin/marketing/AffiliateEditPage'));
const AuditLogListPage = lazy(() => import('../pages/admin/audit-logs/AuditLogListPage'));
const EmailTemplatesPage = lazy(() => import('../pages/admin/settings/EmailTemplatesPage'));
const NotificationSettingsPage = lazy(() => import('../pages/admin/settings/NotificationSettingsPage'));
const NotificationLogPage = lazy(() => import('../pages/admin/settings/NotificationLogPage'));
const DataProcessorsPage = lazy(() => import('../pages/admin/settings/DataProcessorsPage'));
const BillingPlatformSettingsPage = lazy(() => import('../pages/admin/settings/BillingPlatformSettingsPage'));
const VerifactuTaxMappingPage = lazy(() => import('../pages/admin/settings/VerifactuTaxMappingPage'));
const BillingSyncFailuresPage = lazy(() => import('../pages/admin/settings/BillingSyncFailuresPage'));
const InventoryPage = lazy(() => import('../pages/admin/inventory/InventoryPage'));
const InventoryDetailPage = lazy(() => import('../pages/admin/inventory/InventoryDetailPage'));
const RmaDetailPage = lazy(() => import('../pages/admin/inventory/rma/RmaDetailPage'));
const SuppliersListPage = lazy(() => import('../pages/admin/suppliers/SuppliersListPage'));
const SupplierDetailPage = lazy(() => import('../pages/admin/suppliers/SupplierDetailPage'));
const LocationsListPage = lazy(() => import('../pages/admin/locations/LocationsListPage'));
const LocationDetailPage = lazy(() => import('../pages/admin/locations/LocationDetailPage'));
const DepositsListPage = lazy(() => import('../pages/admin/deposits/DepositsListPage'));
const DepositDetailPage = lazy(() => import('../pages/admin/deposits/DepositDetailPage'));
const InstallationListPage = lazy(() => import('../pages/admin/installations/InstallationListPage'));
const ComplianceCenterPage = lazy(() => import('../pages/admin/compliance/ComplianceCenterPage'));
const TasksListPage = lazy(() => import('../pages/admin/tasks/TasksListPage'));
const TaskTemplatesPage = lazy(() => import('../pages/admin/settings/TaskTemplatesPage'));
const CustomerPortalSettingsPage = lazy(() => import('../pages/admin/settings/CustomerPortalSettingsPage'));

/**
 * Admin area routes — all prefixed with /admin/ or /dashboard.
 * Split into logical sub-sections via comments for readability.
 */
export const adminRoutes: RouteObject[] = [
  // --- Customers ---
  {
    path: '/admin/customers',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_CREATE}>
        <CustomerCreatePage />
      </ProtectedRoute>
    ),
  },
  // BUG-E40: /admin/customers/new treats "new" as an ID → infinite spinner.
  // Add explicit redirect BEFORE the :id route so it matches first.
  {
    path: '/admin/customers/new',
    element: <Navigate to="/admin/customers/create" replace />,
  },
  {
    path: '/admin/customers/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_UPDATE}>
        <CustomerEditPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/contacts',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerContactsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/contracts',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerContractsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/credit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerCreditPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/deposits',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerDepositsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/inventory',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerInventoryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/notes',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerNotesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/customers/:id/system-log',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
        <CustomerSystemLogPage />
      </ProtectedRoute>
    ),
  },

  // --- Orders ---
  {
    path: '/admin/orders',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ORDERS_VIEW}>
        <OrderListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/orders/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ORDERS_CREATE}>
        <OrderCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/orders/approval-queue',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ORDERS_APPROVE}>
        <OrderApprovalQueuePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/orders/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ORDERS_VIEW}>
        <OrderDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/orders/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ORDERS_UPDATE}>
        <OrderEditPage />
      </ProtectedRoute>
    ),
  },
  // --- Installations ---
  {
    path: '/admin/installations',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INSTALLATIONS_VIEW}>
        <InstallationListPage />
      </ProtectedRoute>
    ),
  },

  // --- Contracts ---
  {
    path: '/admin/contracts',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ORDERS_VIEW}>
        <ContractListPage />
      </ProtectedRoute>
    ),
  },

  // --- Offers (Presupuestos) ---
  {
    path: '/admin/offers',
    element: (
      <ProtectedRoute permission={PERMISSIONS.OFFERS_VIEW}>
        <OfferListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/offers/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.OFFERS_CREATE}>
        <OfferCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/offers/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.OFFERS_VIEW}>
        <OfferDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/offers/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.OFFERS_MANAGE}>
        <OfferEditPage />
      </ProtectedRoute>
    ),
  },

  // --- Leads (CRM) — W3.1 ---
  {
    path: '/admin/leads',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LEADS_VIEW}>
        <LeadsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/leads/board',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LEADS_VIEW}>
        <LeadsBoardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/leads/reports',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LEADS_VIEW}>
        <LeadsReportPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/leads/new',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LEADS_CREATE}>
        <LeadCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/leads/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LEADS_VIEW}>
        <LeadDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Providers ---
  {
    path: '/admin/providers',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PROVIDERS_VIEW}>
        <ProviderListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/providers/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PROVIDERS_CREATE}>
        <ProviderCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    // W2.5 — must be registered before the /:id dynamic route.
    path: '/admin/providers/deposits',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PROVIDERS_VIEW}>
        <ProviderDepositsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/providers/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PROVIDERS_VIEW}>
        <ProviderDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/providers/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PROVIDERS_UPDATE}>
        <ProviderEditPage />
      </ProtectedRoute>
    ),
  },

  // --- Products ---
  {
    path: '/admin/products',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
        <ProductListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/products/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PRODUCTS_CREATE}>
        <ProductCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/products/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
        <ProductDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/products/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PRODUCTS_UPDATE}>
        <ProductEditPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/products/:id/inventory-config',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PRODUCTS_UPDATE}>
        <ProductInventoryConfigPage />
      </ProtectedRoute>
    ),
  },

  // --- Subscriptions ---
  {
    path: '/admin/subscriptions',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SUBSCRIPTIONS_VIEW}>
        <SubscriptionListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/subscriptions/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SUBSCRIPTIONS_CREATE}>
        <SubscriptionCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/subscriptions/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SUBSCRIPTIONS_VIEW}>
        <SubscriptionDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Inventory ---
  {
    path: '/admin/inventory',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVENTORY_VIEW}>
        <InventoryPage />
      </ProtectedRoute>
    ),
  },
  // #543 — Inventory sub-routes now redirect to in-page tab URLs.
  // The old standalone page components (StockMovementsPage etc.) were
  // dead code (unreachable, zero imports) and were removed; each tab
  // renders its own self-contained tab-content component.
  {
    path: '/admin/inventory/stock',
    element: <Navigate to="/admin/inventory?tab=stock" replace />,
  },
  {
    path: '/admin/inventory/warranty',
    element: <Navigate to="/admin/inventory?tab=warranty" replace />,
  },
  {
    path: '/admin/inventory/sales',
    element: <Navigate to="/admin/inventory?tab=sales" replace />,
  },
  {
    path: '/admin/inventory/leases',
    element: <Navigate to="/admin/inventory?tab=leases" replace />,
  },
  {
    path: '/admin/inventory/returns',
    element: <Navigate to="/admin/inventory?tab=returns" replace />,
  },
  {
    path: '/admin/inventory/rma',
    element: <Navigate to="/admin/inventory?tab=rma" replace />,
  },
  {
    path: '/admin/inventory/rma/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVENTORY_VIEW}>
        <RmaDetailPage />
      </ProtectedRoute>
    ),
  },
  // BUG-E19: /admin/inventory/devices was matching the :id param route and
  // rendering "Inventory item not found". Guard the known non-ID segments with
  // specific redirects before the catch-all :id route.
  {
    path: '/admin/inventory/devices',
    element: <Navigate to="/admin/inventory?tab=items" replace />,
  },
  {
    path: '/admin/inventory/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVENTORY_VIEW}>
        <InventoryDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Suppliers ---
  {
    path: '/admin/suppliers',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_MANAGE}>
        <SuppliersListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/suppliers/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_MANAGE}>
        <SupplierDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Locations ---
  {
    path: '/admin/locations',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LOCATIONS_MANAGE}>
        <LocationsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/locations/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LOCATIONS_MANAGE}>
        <LocationDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Deposits ---
  {
    path: '/admin/deposits',
    element: (
      <ProtectedRoute permission={PERMISSIONS.DEPOSITS_VIEW}>
        <DepositsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/deposits/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.DEPOSITS_VIEW}>
        <DepositDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Invoices ---
  {
    path: '/admin/invoices',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVOICES_VIEW}>
        <InvoiceListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/invoices/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVOICES_CREATE}>
        <InvoiceCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/invoices/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVOICES_VIEW}>
        <InvoiceDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/invoices/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.INVOICES_UPDATE}>
        <InvoiceEditPage />
      </ProtectedRoute>
    ),
  },

  // --- Billing ---
  // BUG-E39: /admin/dunning is the natural short URL; redirect to canonical path.
  {
    path: '/admin/dunning',
    element: <Navigate to="/admin/billing/dunning" replace />,
  },
  // BUG-E57: /admin/billing/payments is a natural deep-link for the Finance
  // group (alongside /admin/billing/dunning). Redirect to canonical path.
  {
    path: '/admin/billing/payments',
    element: <Navigate to="/admin/payments" replace />,
  },
  {
    path: '/admin/billing/dunning',
    element: (
      <ProtectedRoute permission={PERMISSIONS.BILLING_VIEW_DUNNING}>
        <DunningQueuePage />
      </ProtectedRoute>
    ),
  },

  // --- Operations ---
  // #178: /admin/email-review is the intuitive short URL; redirect to canonical path.
  {
    path: '/admin/email-review',
    element: <Navigate to="/admin/operations/email-review" replace />,
  },
  {
    path: '/admin/operations/email-review',
    element: (
      <ProtectedRoute permission={PERMISSIONS.EMAIL_REVIEW_VIEW}>
        <EmailReviewQueuePage />
      </ProtectedRoute>
    ),
  },
  // BUG-E44: /admin/llm-usage is the intuitive short URL; redirect to canonical path.
  {
    path: '/admin/llm-usage',
    element: <Navigate to="/admin/operations/llm-usage" replace />,
  },
  {
    path: '/admin/operations/llm-usage',
    element: (
      <ProtectedRoute permission={PERMISSIONS.LLM_USAGE_VIEW}>
        <LlmUsagePage />
      </ProtectedRoute>
    ),
  },

  // --- Payments ---
  {
    path: '/admin/payments',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PAYMENTS_VIEW}>
        <PaymentListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/payments/gateway-log',
    element: (
      <ProtectedRoute permission={PERMISSIONS.GATEWAY_TRANSACTIONS_VIEW}>
        <GatewayTransactionLogPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/payments/gateway-log/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.GATEWAY_TRANSACTIONS_VIEW}>
        <GatewayTransactionDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/payments/disputes',
    element: (
      <ProtectedRoute permission={PERMISSIONS.DISPUTES_VIEW}>
        <DisputeListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/payments/disputes/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.DISPUTES_VIEW}>
        <DisputeDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/payments/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.PAYMENTS_VIEW}>
        <PaymentDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Reconciliation (M4-F triage skeleton) ---
  {
    path: '/admin/reconciliation/transactions',
    element: (
      <ProtectedRoute permission={PERMISSIONS.RECONCILIATION_VIEW}>
        <ReconciliationTransactionListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/reconciliation/transactions/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.RECONCILIATION_VIEW}>
        <ReconciliationTransactionDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Tasks ---
  {
    path: '/admin/tasks',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TASKS_VIEW}>
        <TasksListPage />
      </ProtectedRoute>
    ),
  },

  // --- Tickets ---
  {
    path: '/admin/tickets',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TICKETS_VIEW}>
        <TicketListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/tickets/dashboard',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TICKETS_VIEW}>
        <TicketDashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/tickets/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TICKETS_CREATE}>
        <TicketCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/tickets/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TICKETS_VIEW}>
        <TicketDetailPage />
      </ProtectedRoute>
    ),
  },

  // --- Reports ---
  // BUG-E20: /admin/reports has no page of its own — redirect to the first
  // sub-report so bookmarks / direct URL access land somewhere sensible.
  {
    path: '/admin/reports',
    element: <Navigate to="/admin/reports/revenue" replace />,
  },
  {
    path: '/admin/reports/revenue',
    element: (
      <ProtectedRoute permission={PERMISSIONS.REPORTS_VIEW}>
        <RevenueReportPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/reports/mrr-waterfall',
    element: (
      <ProtectedRoute permission={PERMISSIONS.REPORTS_VIEW}>
        <MrrWaterfallReportPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/reports/subscriptions',
    element: (
      <ProtectedRoute permission={PERMISSIONS.REPORTS_VIEW}>
        <SubscriptionReportPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/reports/tickets',
    element: (
      <ProtectedRoute permission={PERMISSIONS.REPORTS_VIEW}>
        <TicketReportPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/reports/deposits',
    element: (
      <ProtectedRoute permission={PERMISSIONS.REPORTS_VIEW}>
        <DepositsReportPage />
      </ProtectedRoute>
    ),
  },

  // --- Marketing ---
  {
    path: '/admin/marketing',
    element: (
      <ProtectedRoute permission={PERMISSIONS.COUPONS_VIEW}>
        <MarketingDashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing/coupons',
    element: (
      <ProtectedRoute permission={PERMISSIONS.COUPONS_VIEW}>
        <CouponsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing/coupons/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.COUPONS_CREATE}>
        <CouponCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing/coupons/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.COUPONS_UPDATE}>
        <CouponEditPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing/affiliates',
    element: (
      <ProtectedRoute permission={PERMISSIONS.AFFILIATES_VIEW}>
        <AffiliatesListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing/affiliates/create',
    element: (
      <ProtectedRoute permission={PERMISSIONS.AFFILIATES_CREATE}>
        <AffiliateCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/marketing/affiliates/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.AFFILIATES_UPDATE}>
        <AffiliateEditPage />
      </ProtectedRoute>
    ),
  },

  // BUG-E20: /admin/workflows has no page — redirect to the settings workflows
  // page. The sidebar never links here directly, but bookmarks / old links may.
  {
    path: '/admin/workflows',
    element: <Navigate to="/admin/settings/workflows" replace />,
  },

  // --- Compliance ---
  {
    path: '/admin/compliance',
    element: (
      <ProtectedRoute permission={PERMISSIONS.DATA_PROTECTION_VIEW}>
        <ComplianceCenterPage />
      </ProtectedRoute>
    ),
  },

  // --- Audit Logs ---
  // R-10 (Issue #157 §4): Removed the duplicate /admin/audit-logs entry (lines 829-836 in the original).
  // The second entry (AuditLogsPage) was unreachable because React Router uses the first matching route.
  // AuditLogListPage is the current implementation; AuditLogsPage was a legacy alias.
  {
    path: '/admin/audit-logs',
    element: (
      <ProtectedRoute permission={PERMISSIONS.AUDIT_LOGS_VIEW}>
        <AuditLogListPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Index redirect ---
  // BUG-E16: /admin/settings (no sub-path) previously matched no route, causing the
  // catch-all `*` to redirect to /dashboard. Redirect to /admin/settings/company.
  {
    path: '/admin/settings',
    element: <Navigate to="/admin/settings/company" replace />,
  },

  // --- Settings: Guessable-URL redirects (#214, #215, #216) ---
  // Users may type natural URL slugs based on sidebar labels; these routes redirect to
  // the canonical paths so they land on a working page rather than a 404.
  //
  // /admin/settings/system       — "System" is a sidebar category heading, not a page;
  //                                redirect to the first item in that category.
  {
    path: '/admin/settings/system',
    element: <Navigate to="/admin/settings/workflows" replace />,
  },
  // /admin/settings/order-workflows — sidebar label is "Order Workflows", actual route is "workflows".
  {
    path: '/admin/settings/order-workflows',
    element: <Navigate to="/admin/settings/workflows" replace />,
  },
  // /admin/settings/roles-permissions — sidebar label is "Roles & Permissions", actual route is "roles".
  {
    path: '/admin/settings/roles-permissions',
    element: <Navigate to="/admin/settings/roles" replace />,
  },
  // /admin/settings/workflow-templates — no dedicated page; templates live inside WorkflowSettingsPage.
  {
    path: '/admin/settings/workflow-templates',
    element: <Navigate to="/admin/settings/workflows" replace />,
  },

  // --- Settings: Users ---
  {
    path: '/admin/settings/users',
    element: (
      <ProtectedRoute permission={PERMISSIONS.USERS_VIEW}>
        <UserListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/users/new',
    element: (
      <ProtectedRoute permission={PERMISSIONS.USERS_CREATE}>
        <UserCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/users/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.USERS_VIEW}>
        <UserDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/users/:id/edit',
    element: (
      <ProtectedRoute permission={PERMISSIONS.USERS_UPDATE}>
        <UserEditPage />
      </ProtectedRoute>
    ),
  },

  // --- Own account: Security (W1.2 — 2FA enrol/disable/regenerate) ---
  {
    path: '/profile/security',
    element: (
      <ProtectedRoute>
        <ProfileSecurityPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Roles & Permissions ---
  {
    path: '/admin/settings/roles',
    element: (
      <ProtectedRoute permission={PERMISSIONS.ROLES_VIEW}>
        <RolesPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Security (W1.2 — 2FA enforcement toggle) ---
  {
    path: '/admin/settings/security',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <SecuritySettingsPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Company & General ---
  {
    path: '/admin/settings/company',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <CompanySettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/contact-defaults',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <ContactDefaultsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/contact-roles',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <ContactRolesPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Email & Notifications ---
  // BUG-E56: /admin/settings/notifications was the expected URL but no route existed.
  // Redirect to the canonical notification-settings page.
  {
    path: '/admin/settings/notifications',
    element: <Navigate to="/admin/settings/notification-settings" replace />,
  },
  {
    path: '/admin/settings/email',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <EmailSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/email-templates',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <EmailTemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/notification-settings',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <NotificationSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/notification-log',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <NotificationLogPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Languages (P3 F9 — multi-language) ---
  {
    path: '/admin/settings/languages',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_MANAGE_LANGUAGES}>
        <LanguagesSettingsPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Billing & Payments ---
  {
    path: '/admin/settings/billing',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <BillingSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/payment-methods',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <PaymentMethodsSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/payment-gateways',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <PaymentGatewaySettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/invoice-templates',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <InvoiceTemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/billing-platform',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <BillingPlatformSettingsPage />
      </ProtectedRoute>
    ),
  },
  // #150 — the FD mapping sub-pages were merged into BillingPlatformSettingsPage
  // tabs (?tab=invoice-payment-method / ?tab=bank-mapping). The one-release-cycle
  // deprecation redirects were removed in P4/D-10 after the redirect cycle
  // completed; no remaining inbound links reference the old path segments.
  {
    path: '/admin/settings/billing-sync-failures',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <BillingSyncFailuresPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/verifactu-tax-mapping',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <VerifactuTaxMappingPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/e-signatures',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <DocumensoSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/ai-provider',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <AnthropicSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/fees',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <FeesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/custom-fields',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <CustomFieldsPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: Support ---
  {
    path: '/admin/settings/ticket-settings',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <TicketSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/channels',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <ChannelsSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/sla-policies',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <SlaPoliciesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/canned-responses',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TICKETS_VIEW}>
        <CannedResponsesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/ticket-tags',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <TicketTagsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/task-templates',
    element: (
      <ProtectedRoute permission={PERMISSIONS.TASK_TEMPLATES_MANAGE}>
        <TaskTemplatesPage />
      </ProtectedRoute>
    ),
  },

  // --- Settings: System ---
  {
    path: '/admin/settings/workflows',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <WorkflowSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/workflow-health',
    element: (
      <ProtectedRoute permission={PERMISSIONS.WORKFLOWS_VIEW}>
        <WorkflowHealthPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/contract-templates',
    element: (
      <ProtectedRoute permission={PERMISSIONS.CONTRACTS_MANAGE_TEMPLATES}>
        <ContractTemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/workflows/wizard',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <WorkflowWizardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/workflows/wizard/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <WorkflowWizardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/workflows/:id',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <WorkflowEditorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/inventory-statuses',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <InventoryStatusesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/provider-types',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <ProviderTypesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/product-types',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
        <ProductTypesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/pause-types',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <PauseTypesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/webhooks',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <WebhooksPage />
      </ProtectedRoute>
    ),
  },
  // #240 — /admin/settings/data-protection is now consolidated into the
  // Compliance Center (/admin/compliance) which has all GDPR/LOPDGDD
  // operational tabs plus the absorbed Backups and Privacy & Retention tabs.
  // Keep the route as a permanent redirect so bookmarks/external links still work.
  {
    path: '/admin/settings/data-protection',
    element: <Navigate to="/admin/compliance" replace />,
  },
  {
    path: '/admin/settings/data-protection/processors',
    element: (
      <ProtectedRoute permission={PERMISSIONS.DATA_PROTECTION_VIEW}>
        <DataProcessorsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings/customer-portal',
    element: (
      <ProtectedRoute permission={PERMISSIONS.SETTINGS_UPDATE}>
        <CustomerPortalSettingsPage />
      </ProtectedRoute>
    ),
  },

  // --- Dev-only design showcases (never rendered in production builds) ---
  ...(import.meta.env.DEV
    ? [
        {
          path: '/admin/dev/orders-design-foundations',
          lazy: () =>
            import('../pages/admin/dev/OrdersDesignFoundationsPage').then((m) => ({
              Component: m.default,
            })),
        },
      ]
    : []),
];
